import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/lib/contexts/auth-context'
import { useTheme } from '@/lib/contexts/theme-context'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/brand/Logo'
import {
  Search,
  ChevronDown,
  User,
  Settings,
  LogOut,
  Moon,
  Sun,
  MessageSquare,
  HelpCircle,
  Menu,
  Command,
  PanelLeftClose,
  PanelLeft,
} from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as Dialog from '@radix-ui/react-dialog'
import { NotificationsDropdown } from './notifications-dropdown'

interface HeaderProps {
  onMenuClick?: () => void
  sidebarCollapsed?: boolean
  onSidebarCollapsedChange?: (collapsed: boolean) => void
  className?: string
}

export function Header({ onMenuClick, sidebarCollapsed, onSidebarCollapsedChange, className }: HeaderProps) {
  const { user, logout } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()
  const [searchOpen, setSearchOpen] = useState(false)

  const toggleTheme = () => {
    // Toggle between light and dark (skip system for quick toggle)
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
  }

  const handleLogout = async () => {
    await logout()
  }

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Get user initials
  const userInitials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase()

  return (
    <header
      className={cn(
        'sticky top-0 z-30 h-14 flex items-center justify-between px-4 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800',
        className
      )}
    >
      {/* Left section */}
      <div className="flex items-center gap-1">
        {/* Mobile menu button */}
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-md text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Mobile Logo */}
        <Link to="/dashboard" className="lg:hidden">
          <Logo variant="icon" size="sm" />
        </Link>

        {/* Sidebar collapse toggle - desktop only */}
        {onSidebarCollapsedChange && (
          <button
            onClick={() => onSidebarCollapsedChange(!sidebarCollapsed)}
            className="hidden lg:flex p-2 rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200 transition-colors"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? (
              <PanelLeft className="h-[18px] w-[18px]" />
            ) : (
              <PanelLeftClose className="h-[18px] w-[18px]" />
            )}
          </button>
        )}

        {/* Search */}
        <Dialog.Root open={searchOpen} onOpenChange={setSearchOpen}>
          <Dialog.Trigger asChild>
            <button className="flex items-center gap-2 h-8 px-3 ml-2 rounded-md bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors w-56 lg:w-72">
              <Search className="h-4 w-4" />
              <span className="text-sm">Search...</span>
              <kbd className="ml-auto hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-white dark:bg-neutral-700 text-[11px] font-medium text-neutral-500 dark:text-neutral-400 border border-neutral-200 dark:border-neutral-600">
                <Command className="h-3 w-3" />K
              </kbd>
            </button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50" />
            <Dialog.Content className="fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-xl bg-white dark:bg-neutral-900 rounded-lg shadow-2xl z-50 overflow-hidden border border-neutral-200 dark:border-neutral-700">
              <div className="p-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-neutral-50 dark:bg-neutral-800 rounded-md">
                  <Search className="h-4 w-4 text-neutral-400" />
                  <input
                    type="text"
                    placeholder="Search templates, assets, users..."
                    className="flex-1 bg-transparent text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 text-sm focus:outline-none"
                    autoFocus
                  />
                  <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-neutral-700 text-[11px] font-medium text-neutral-500 dark:text-neutral-400 border border-neutral-200 dark:border-neutral-600">
                    ESC
                  </kbd>
                </div>
              </div>
              <div className="px-3 pb-3">
                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-2 px-1">
                  Recent searches
                </p>
                <div className="space-y-0.5">
                  {['Invoice template', 'Marketing assets', 'User management'].map((item) => (
                    <button
                      key={item}
                      className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                    >
                      <Search className="h-4 w-4 text-neutral-400" />
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>

      {/* Right section */}
      <div className="flex items-center gap-1">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200 transition-colors"
          aria-label="Toggle theme"
        >
          {resolvedTheme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </button>

        {/* Notifications */}
        <NotificationsDropdown />

        {/* User menu */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button className="flex items-center gap-2 h-9 pl-1 pr-2 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">
              <div className="h-7 w-7 rounded-md bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-neutral-700 dark:text-neutral-200 text-xs font-semibold">
                {userInitials}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 leading-tight">
                  {user?.firstName} {user?.lastName}
                </p>
              </div>
              <ChevronDown className="h-4 w-4 text-neutral-400 hidden md:block" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="min-w-[220px] bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-700 shadow-lg p-1 z-50"
              sideOffset={8}
              align="end"
            >
              {/* User info */}
              <div className="px-2 py-2 mb-1">
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 rounded-md bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-neutral-700 dark:text-neutral-200 text-sm font-semibold">
                    {userInitials}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">{user?.email}</p>
                  </div>
                </div>
              </div>

              <div className="h-px bg-neutral-200 dark:bg-neutral-700 mx-1 my-1" />

              <DropdownMenu.Item asChild>
                <Link
                  to="/profile"
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors cursor-pointer outline-none"
                >
                  <User className="h-4 w-4 text-neutral-500" />
                  Profile
                </Link>
              </DropdownMenu.Item>
              <DropdownMenu.Item asChild>
                <Link
                  to="/settings"
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors cursor-pointer outline-none"
                >
                  <Settings className="h-4 w-4 text-neutral-500" />
                  Settings
                </Link>
              </DropdownMenu.Item>

              <div className="h-px bg-neutral-200 dark:bg-neutral-700 mx-1 my-1" />

              <DropdownMenu.Item asChild>
                <Link
                  to="/feedback"
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors cursor-pointer outline-none"
                >
                  <MessageSquare className="h-4 w-4 text-neutral-500" />
                  Feedback
                </Link>
              </DropdownMenu.Item>
              <DropdownMenu.Item asChild>
                <Link
                  to="/help"
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors cursor-pointer outline-none"
                >
                  <HelpCircle className="h-4 w-4 text-neutral-500" />
                  Help & Support
                </Link>
              </DropdownMenu.Item>

              <div className="h-px bg-neutral-200 dark:bg-neutral-700 mx-1 my-1" />

              <DropdownMenu.Item
                className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors cursor-pointer outline-none"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
                Logout
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  )
}
